import { useState, useEffect, useMemo } from 'react'

import {
  Drawer,
  TextInput,
  Select,
  NumberInput,
  Divider,
  Text,
  ActionIcon,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import { useMutation, useQuery } from '@redwoodjs/web'

import Button from 'src/components/Buttons/Button/Button'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  CREATE_WEBHOOK,
  UPDATE_WEBHOOK,
  WEBHOOK_QUERY,
  WEBHOOKS_QUERY,
} from 'src/graphql/webhooks.graphql'
import IconAdd from 'src/icons/IconAdd'
import IconClose from 'src/icons/IconClose'

// Base schema shared by both create and edit modes
const baseSchema = z.object({
  url: z
    .string()
    .min(1, { message: 'URL is required' })
    .url({ message: 'Invalid URL format' })
    .refine((url) => url.startsWith('https://'), {
      message: 'URL must use HTTPS',
    }),
  eventType: z.string().min(1, { message: 'Event type is required' }),
  authType: z.string().min(1, { message: 'Auth type is required' }),
  oauthTokenUrl: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val || val.trim() === '') return true
        try {
          new URL(val)
          return true
        } catch {
          return false
        }
      },
      { message: 'Invalid OAuth Token URL format' }
    )
    .refine(
      (val) => {
        if (!val || val.trim() === '') return true
        return val.startsWith('https://')
      },
      { message: 'OAuth Token URL must use HTTPS' }
    ),
  clientId: z.string().optional(),
  clientSecret: z.string().optional(),
  timeoutMilliseconds: z.number().min(1000).max(10000).optional(),
})

// Create mode schema - requires clientSecret for OAUTH2
const createSchema = baseSchema.refine(
  (data) => {
    // If authType is OAUTH2, require all OAuth fields including clientSecret
    if (data.authType === 'OAUTH2') {
      return (
        !!data.oauthTokenUrl &&
        data.oauthTokenUrl.trim() !== '' &&
        !!data.clientId &&
        data.clientId.trim() !== '' &&
        !!data.clientSecret &&
        data.clientSecret.trim() !== ''
      )
    }
    return true
  },
  {
    message: 'OAuth2 requires Token URL, Client ID, and Client Secret',
    path: ['oauthTokenUrl'],
  }
)

// Edit mode schema - clientSecret is optional for OAUTH2 (since it's not displayed for security)
const editSchema = baseSchema.refine(
  (data) => {
    // If authType is OAUTH2, require Token URL and Client ID only
    // clientSecret is optional in edit mode since it's not shown for security
    if (data.authType === 'OAUTH2') {
      return (
        !!data.oauthTokenUrl &&
        data.oauthTokenUrl.trim() !== '' &&
        !!data.clientId &&
        data.clientId.trim() !== ''
      )
    }
    return true
  },
  {
    message: 'OAuth2 requires Token URL and Client ID',
    path: ['oauthTokenUrl'],
  }
)

interface WebhookDrawerProps {
  opened: boolean
  close: () => void
  webhookId?: string | null
}

const WebhookDrawer = ({ opened, close, webhookId }: WebhookDrawerProps) => {
  const [headers, setHeaders] = useState<Array<{ key: string; value: string }>>(
    []
  )
  const [headerErrors, setHeaderErrors] = useState<
    Array<{ key?: string; value?: string }>
  >([])
  const [oauthParams, setOAuthParams] = useState<
    Array<{ key: string; value: string }>
  >([])
  const [oauthParamErrors, setOAuthParamErrors] = useState<
    Array<{ key?: string; value?: string }>
  >([])
  const isEditMode = !!webhookId

  // Validation constants
  const MAX_HEADER_KEY_LENGTH = 256
  const MAX_HEADER_VALUE_LENGTH = 2048
  const HEADER_KEY_REGEX = /^[a-zA-Z0-9_-]+$/

  // Use different schemas for create vs edit mode
  const schema = useMemo(
    () => (isEditMode ? editSchema : createSchema),
    [isEditMode]
  )

  const form = useForm({
    initialValues: {
      url: '',
      eventType: 'APPLICANT_STATUS_CHANGED',
      authType: 'NONE',
      oauthTokenUrl: '',
      clientId: '',
      clientSecret: '',
      timeoutMilliseconds: 5000,
    },
    validate: zodResolver(schema),
  })

  // Fetch webhook details when editing
  const { data: webhookData, loading: webhookLoading } = useQuery(
    WEBHOOK_QUERY,
    {
      variables: { webhookId },
      skip: !webhookId,
      fetchPolicy: 'network-only',
    }
  )

  // Reset form when switching modes or opening drawer
  useEffect(() => {
    if (opened) {
      if (!webhookId) {
        // Create mode: reset form to initial values
        form.reset()
        setHeaders([])
        setHeaderErrors([])
        setOAuthParams([])
        setOAuthParamErrors([])
      }
    }
  }, [opened, webhookId])

  // Populate form when webhook data is loaded
  useEffect(() => {
    if (webhookData?.webhook) {
      const webhook = webhookData.webhook
      form.setValues({
        url: webhook.url || '',
        eventType: webhook.eventType || 'APPLICANT_STATUS_CHANGED',
        authType: webhook.authType || 'NONE',
        oauthTokenUrl: webhook.oauthTokenUrl || '',
        clientId: webhook.clientId || '',
        clientSecret: '', // Don't populate client secret for security
        timeoutMilliseconds: webhook.timeoutMilliseconds || 5000,
      })

      // Set headers if they exist
      if (webhook.headers && webhook.headers.length > 0) {
        const loadedHeaders = webhook.headers
          .filter((h) => !h.isAuth) // Filter out auth headers
          .map((h) => ({ key: h.key, value: h.value }))
        setHeaders(loadedHeaders)
        // Initialize errors array with empty objects for loaded headers
        setHeaderErrors(loadedHeaders.map(() => ({})))
      } else {
        setHeaders([])
        setHeaderErrors([])
      }

      // Set oauthParams if they exist
      if (webhook.oauthParams && webhook.oauthParams.length > 0) {
        const loadedOAuthParams = webhook.oauthParams.map((h) => ({
          key: h.key,
          value: h.value,
        }))
        setOAuthParams(loadedOAuthParams)
        // Initialize errors array with empty objects for loaded auth headers
        setOAuthParamErrors(loadedOAuthParams.map(() => ({})))
      } else {
        setOAuthParams([])
        setOAuthParamErrors([])
      }
    }
  }, [webhookData])

  const validateHeader = (
    index: number,
    field: 'key' | 'value',
    value: string
  ) => {
    const newErrors = [...headerErrors]
    if (!newErrors[index]) {
      newErrors[index] = {}
    }

    if (field === 'key') {
      if (value.trim() === '') {
        newErrors[index].key = 'Key is required'
      } else if (!HEADER_KEY_REGEX.test(value)) {
        newErrors[index].key =
          'Only letters, numbers, hyphens, and underscores allowed'
      } else if (value.length > MAX_HEADER_KEY_LENGTH) {
        newErrors[index].key = `Maximum ${MAX_HEADER_KEY_LENGTH} characters`
      } else {
        // Check for duplicate keys
        const duplicateIndex = headers.findIndex(
          (h, i) =>
            i !== index &&
            h.key.toLowerCase() === value.toLowerCase() &&
            value.length > 0
        )
        if (duplicateIndex !== -1) {
          newErrors[index].key = 'Duplicate header key'
        } else {
          delete newErrors[index].key
        }
      }
    } else if (field === 'value') {
      if (value.trim() === '') {
        newErrors[index].value = 'Value is required'
      } else if (value.length > MAX_HEADER_VALUE_LENGTH) {
        newErrors[index].value = `Maximum ${MAX_HEADER_VALUE_LENGTH} characters`
      } else {
        delete newErrors[index].value
      }
    }

    setHeaderErrors(newErrors)
  }

  const addHeader = () => {
    setHeaders([...headers, { key: '', value: '' }])
    setHeaderErrors([...headerErrors, {}])
  }

  const removeHeader = (index: number) => {
    setHeaders(headers.filter((_, i) => i !== index))
    setHeaderErrors(headerErrors.filter((_, i) => i !== index))
  }

  const updateHeader = (
    index: number,
    field: 'key' | 'value',
    value: string
  ) => {
    const newHeaders = [...headers]
    newHeaders[index][field] = value
    setHeaders(newHeaders)
    validateHeader(index, field, value)
  }

  // Auth Header Management Functions
  const validateOAuthParam = (
    index: number,
    field: 'key' | 'value',
    value: string
  ) => {
    const newErrors = [...oauthParamErrors]
    if (!newErrors[index]) {
      newErrors[index] = {}
    }

    if (field === 'key') {
      if (value.trim() === '') {
        newErrors[index].key = 'Key is required'
      } else if (!HEADER_KEY_REGEX.test(value)) {
        newErrors[index].key =
          'Only letters, numbers, hyphens, and underscores allowed'
      } else if (value.length > MAX_HEADER_KEY_LENGTH) {
        newErrors[index].key = `Maximum ${MAX_HEADER_KEY_LENGTH} characters`
      } else {
        // Check for duplicate keys
        const duplicateIndex = oauthParams.findIndex(
          (h, i) =>
            i !== index &&
            h.key.toLowerCase() === value.toLowerCase() &&
            value.length > 0
        )
        if (duplicateIndex !== -1) {
          newErrors[index].key = 'Duplicate OAuth parameter key'
        } else {
          delete newErrors[index].key
        }
      }
    } else if (field === 'value') {
      if (value.trim() === '') {
        newErrors[index].value = 'Value is required'
      } else if (value.length > MAX_HEADER_VALUE_LENGTH) {
        newErrors[index].value = `Maximum ${MAX_HEADER_VALUE_LENGTH} characters`
      } else {
        delete newErrors[index].value
      }
    }

    setOAuthParamErrors(newErrors)
  }

  const addOAuthParam = () => {
    setOAuthParams([...oauthParams, { key: '', value: '' }])
    setOAuthParamErrors([...oauthParamErrors, {}])
  }

  const removeOAuthParam = (index: number) => {
    setOAuthParams(oauthParams.filter((_, i) => i !== index))
    setOAuthParamErrors(oauthParamErrors.filter((_, i) => i !== index))
  }

  const updateOAuthParam = (
    index: number,
    field: 'key' | 'value',
    value: string
  ) => {
    const newOAuthParams = [...oauthParams]
    newOAuthParams[index][field] = value
    setOAuthParams(newOAuthParams)
    validateOAuthParam(index, field, value)
  }

  const [createWebhook, { loading: createLoading }] = useMutation(
    CREATE_WEBHOOK,
    {
      refetchQueries: [WEBHOOKS_QUERY],
      onCompleted: () => {
        toast('Webhook created successfully!', 'success')
        form.reset()
        setHeaders([])
        setHeaderErrors([])
        setOAuthParams([])
        setOAuthParamErrors([])
        close()
      },
      onError: (error) => {
        toast(
          error?.message || 'Unable to create webhook, please try again.',
          'error'
        )
      },
    }
  )

  const [updateWebhook, { loading: updateLoading }] = useMutation(
    UPDATE_WEBHOOK,
    {
      refetchQueries: [WEBHOOKS_QUERY],
      onCompleted: () => {
        toast('Webhook updated successfully!', 'success')
        form.reset()
        setHeaders([])
        setHeaderErrors([])
        setOAuthParams([])
        setOAuthParamErrors([])
        close()
      },
      onError: (error) => {
        toast(
          error?.message || 'Unable to update webhook, please try again.',
          'error'
        )
      },
    }
  )

  const loading = createLoading || updateLoading || webhookLoading

  const onSubmit = (values) => {
    const input: any = {
      url: values.url,
      eventType: values.eventType,
      authType: values.authType,
      timeoutMilliseconds: values.timeoutMilliseconds,
    }

    // Only include OAuth fields if authType is OAUTH2
    if (values.authType === 'OAUTH2') {
      input.oauthTokenUrl = values.oauthTokenUrl
      input.clientId = values.clientId
      // Only include client secret if it has been changed (not empty)
      if (values.clientSecret && values.clientSecret.trim() !== '') {
        input.clientSecret = values.clientSecret
      }
    }

    // Include headers for both create and update
    const validHeaders = headers.filter(
      (h) => h.key.trim() !== '' && h.value.trim() !== ''
    )
    input.headers = validHeaders

    // Include oauthParams for both create and update
    const validOAuthParams = oauthParams.filter(
      (h) => h.key.trim() !== '' && h.value.trim() !== ''
    )
    input.oauthParams = validOAuthParams

    if (isEditMode) {
      updateWebhook({
        variables: { webhookId, input },
      })
    } else {
      createWebhook({
        variables: { input },
      })
    }
  }

  const isOAuth2 = form.values.authType === 'OAUTH2'

  return (
    <Drawer
      opened={opened}
      onClose={close}
      position="right"
      withCloseButton={false}
      size="30%"
      offset={6}
      radius="md"
      overlayProps={{ backgroundOpacity: 0.2 }}
      classNames={{
        title: 'text-base font-semibold text-doubleNickel-gray-900',
        body: 'flex flex-col gap-10 h-full',
      }}
    >
      <div className="flex h-full flex-col gap-4">
        <div className="flex h-full flex-1 flex-col gap-3 overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-doubleNickel-gray-900">
              {isEditMode ? 'Edit Webhook' : 'Create Webhook'}
            </h2>
            <Button
              text="Save"
              variant="primary"
              onClick={form.onSubmit(onSubmit)}
              loading={loading}
              size="sm"
            />
          </div>

          <Divider variant="dashed" />

          {/* Form Content */}
          <div className="flex flex-col gap-4 overflow-y-auto">
            <div className="space-y-4">
              <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
                CONFIGURATION
              </Text>
              <div className="space-y-3">
                <div className="flex flex-row items-center justify-between">
                  <Text size="xs">
                    URL
                    <span className="text-doubleNickel-error-500">{' *'}</span>
                  </Text>
                  <div className="w-[250px]">
                    <TextInput
                      placeholder="https://example.com/webhook"
                      size="xs"
                      classNames={{
                        input: 'text-xs text-right',
                      }}
                      {...form.getInputProps('url')}
                    />
                  </div>
                </div>

                <div className="flex flex-row items-center justify-between">
                  <Text size="xs">
                    Event Type
                    <span className="text-doubleNickel-error-500">{' *'}</span>
                  </Text>
                  <div className="w-[250px]">
                    <Select
                      placeholder="Select event type"
                      size="xs"
                      classNames={{
                        input: 'text-xs text-right',
                      }}
                      data={[
                        {
                          value: 'APPLICANT_STATUS_CHANGED',
                          label: 'Applicant Status Changed',
                        },
                        {
                          value: 'JOB_LISTING_UPDATED',
                          label: 'Job Listing Updated',
                        },
                      ]}
                      {...form.getInputProps('eventType')}
                    />
                  </div>
                </div>

                <div className="flex flex-row items-center justify-between">
                  <Text size="xs">Timeout (ms)</Text>
                  <div className="w-[250px]">
                    <NumberInput
                      placeholder="5000"
                      size="xs"
                      min={1000}
                      max={10000}
                      step={100}
                      classNames={{
                        input: 'text-xs text-right',
                      }}
                      {...form.getInputProps('timeoutMilliseconds')}
                    />
                  </div>
                </div>
              </div>
            </div>

            <Divider variant="dashed" />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
                  AUTHENTICATION
                </Text>
                {isOAuth2 && (
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size={24}
                    onClick={addOAuthParam}
                    title="Add headers to OAuth2 token request"
                  >
                    <IconAdd className="h-4 fill-none stroke-doubleNickel-gray-500" />
                  </ActionIcon>
                )}
              </div>
              <div className="space-y-3">
                <div className="flex flex-row items-center justify-between">
                  <Text size="xs">
                    Auth Type
                    <span className="text-doubleNickel-error-500">{' *'}</span>
                  </Text>
                  <div className="w-[250px]">
                    <Select
                      placeholder="Select auth type"
                      size="xs"
                      classNames={{
                        input: 'text-xs text-right',
                      }}
                      data={[
                        { value: 'NONE', label: 'None' },
                        { value: 'OAUTH2', label: 'OAuth 2.0' },
                      ]}
                      {...form.getInputProps('authType')}
                    />
                  </div>
                </div>

                {isOAuth2 && (
                  <>
                    <div className="flex flex-row items-center justify-between">
                      <Text size="xs">
                        Token URL
                        <span className="text-doubleNickel-error-500">
                          {' *'}
                        </span>
                      </Text>
                      <div className="w-[250px]">
                        <TextInput
                          placeholder="https://example.com/oauth/token"
                          size="xs"
                          classNames={{
                            input: 'text-xs text-right',
                          }}
                          {...form.getInputProps('oauthTokenUrl')}
                        />
                      </div>
                    </div>

                    <div className="flex flex-row items-center justify-between">
                      <Text size="xs">
                        Client ID
                        <span className="text-doubleNickel-error-500">
                          {' *'}
                        </span>
                      </Text>
                      <div className="w-[250px]">
                        <TextInput
                          placeholder="Client ID"
                          size="xs"
                          classNames={{
                            input: 'text-xs text-right',
                          }}
                          {...form.getInputProps('clientId')}
                        />
                      </div>
                    </div>

                    <div className="flex flex-row items-center justify-between">
                      <Text size="xs">
                        Client Secret
                        {!isEditMode && (
                          <span className="text-doubleNickel-error-500">
                            {' *'}
                          </span>
                        )}
                      </Text>
                      <div className="w-[250px]">
                        <TextInput
                          placeholder={
                            isEditMode
                              ? 'Leave empty to keep current'
                              : 'Client Secret'
                          }
                          type="password"
                          size="xs"
                          classNames={{
                            input: 'text-xs text-right',
                          }}
                          {...form.getInputProps('clientSecret')}
                        />
                      </div>
                    </div>

                    {oauthParams.length > 0 && (
                      <>
                        <Divider variant="dashed" />
                        <div className="space-y-3">
                          {oauthParams.map((oauthParam, index) => (
                            <div key={index}>
                              <div className="flex flex-row items-start gap-2">
                                <TextInput
                                  placeholder="Key"
                                  size="xs"
                                  value={oauthParam.key}
                                  onChange={(e) =>
                                    updateOAuthParam(
                                      index,
                                      'key',
                                      e.target.value
                                    )
                                  }
                                  error={oauthParamErrors[index]?.key}
                                  classNames={{
                                    input: 'text-xs',
                                  }}
                                  className="flex-1"
                                />
                                <TextInput
                                  placeholder="Value"
                                  size="xs"
                                  value={oauthParam.value}
                                  onChange={(e) =>
                                    updateOAuthParam(
                                      index,
                                      'value',
                                      e.target.value
                                    )
                                  }
                                  error={oauthParamErrors[index]?.value}
                                  classNames={{
                                    input: 'text-xs',
                                  }}
                                  className="flex-1"
                                />
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  size={24}
                                  onClick={() => removeOAuthParam(index)}
                                  className="mt-0.5"
                                >
                                  <IconClose className="h-4 fill-none stroke-doubleNickel-gray-500" />
                                </ActionIcon>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            <Divider variant="dashed" />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
                  HEADERS
                </Text>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size={24}
                  onClick={addHeader}
                >
                  <IconAdd className="h-4 fill-none stroke-doubleNickel-gray-500" />
                </ActionIcon>
              </div>
              <div className="space-y-3">
                {headers.map((header, index) => (
                  <div key={index}>
                    <div className="flex flex-row items-start gap-2">
                      <TextInput
                        placeholder="Key"
                        size="xs"
                        value={header.key}
                        onChange={(e) =>
                          updateHeader(index, 'key', e.target.value)
                        }
                        error={headerErrors[index]?.key}
                        classNames={{
                          input: 'text-xs',
                        }}
                        className="flex-1"
                      />
                      <TextInput
                        placeholder="Value"
                        size="xs"
                        value={header.value}
                        onChange={(e) =>
                          updateHeader(index, 'value', e.target.value)
                        }
                        error={headerErrors[index]?.value}
                        classNames={{
                          input: 'text-xs',
                        }}
                        className="flex-1"
                      />
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size={24}
                        onClick={() => removeHeader(index)}
                        className="mt-0.5"
                      >
                        <IconClose className="h-4 fill-none stroke-doubleNickel-gray-500" />
                      </ActionIcon>
                    </div>
                  </div>
                ))}
                {headers.length === 0 && (
                  <Text
                    size="xs"
                    className="py-2 text-center text-doubleNickel-gray-400"
                  >
                    No headers added. Click + to add a header.
                  </Text>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  )
}

export default WebhookDrawer
